<template>
  <div class="dropdown lang_dropdown">
    <button
      class="btn dropdown-toggle"
      type="button"
      id="langDropdown"
      data-toggle="dropdown"
      aria-haspopup="true"
      aria-expanded="false"
    >
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
        <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="#1D5166" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M7.99961 3H8.99961C7.04961 8.84 7.04961 15.16 8.99961 21H7.99961" stroke="#1D5166" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M15 3C16.95 8.84 16.95 15.16 15 21" stroke="#1D5166" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M3 16V15C8.84 16.95 15.16 16.95 21 15V16" stroke="#1D5166" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M3 8.99998C8.84 7.04998 15.16 7.04998 21 8.99998" stroke="#1D5166" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
      {{ currentLanguageLabel }}
      <i class="fas fa-chevron-down"></i>
    </button>

    <div class="dropdown-menu" aria-labelledby="langDropdown">
      <a
        v-for="lang in availableLanguages"
        :key="lang.code"
        class="dropdown-item"
        href="#"
        @click.prevent="switchLanguage(lang.code)"
        :class="{ active: currentLocale === lang.code }"
      >
        {{ lang.native_name }}
      </a>
      
      <!-- Fallback if no languages loaded -->
      <template v-if="availableLanguages.length === 0">
        <a
          class="dropdown-item"
          href="#"
          @click.prevent="switchLanguage('ar')"
          :class="{ active: currentLocale === 'ar' }"
        >
          العربية
        </a>
        <a
          class="dropdown-item"
          href="#"
          @click.prevent="switchLanguage('en')"
          :class="{ active: currentLocale === 'en' }"
        >
          English
        </a>
      </template>
    </div>
  </div>
</template>

<script>
import StorageService from '@services/storage.service';
import ApiService from '@services/api.service';
import Language from '@api/language.api';
import axios from 'axios';
import eBus from '@/event-bus';

export default {
  name: 'LanguageDropdown',

  data() {
    return {
      currentLocale: 'ar',
      availableLanguages: [],
      loadingLanguages: false,
    };
  },

  computed: {
    currentLanguageLabel() {
      // Try to find from fetched languages first
      const currentLang = this.availableLanguages.find(lang => lang.code === this.currentLocale);
      if (currentLang) {
        return currentLang.native_name;
      }
      
      // Fallback to hardcoded labels
      if (this.currentLocale === 'ar') return 'العربية';
      if (this.currentLocale === 'ar_kw') return 'العربية (الكويت)';
      if (this.currentLocale === 'ar_sa') return 'العربية (السعودية)';
      return 'English';
    },
  },

  mounted() {
    this.loadSavedLocale();
    this.fetchActiveLanguages();
    this.$nextTick(() => {
      if (window.$) {
        $('[data-toggle="dropdown"]').dropdown();
      }
    });
  },

  methods: {
    async fetchActiveLanguages() {
      this.loadingLanguages = true;
      try {
        const response = await Language.active();
        if (response && response.data) {
          this.availableLanguages = response.data.data || response.data || [];
        }
      } catch (error) {
        console.error('Failed to fetch active languages:', error);
        // Fallback to default languages on error
        this.availableLanguages = [
          { code: 'ar', name: 'Arabic', native_name: 'العربية', is_active: true },
          { code: 'en', name: 'English', native_name: 'English', is_active: true },
        ];
      } finally {
        this.loadingLanguages = false;
      }
    },

    async switchLanguage(locale) {
      console.log('Language changed to:', locale);
      if (locale === this.currentLocale) return;

      try {
        this.setLocale(locale);
        
        // Ensure backend knows about the change (optional if stateless)
        axios.post("/change-language", { locale: locale }).catch(console.error);

        // Update CSS and Dir
        this.changeDir(locale);
        
        eBus.$emit('locale-changed', locale);

        // No reload!
        // window.location.reload();
      } catch (error) {
        console.error('Language switch failed:', error);
        if (this.$notify) {
             this.$notify.error('Language switch failed. Please try again.');
        }
      }
    },

    setLocale(lang) {
      this.saveLocale(lang);
      this.currentLocale = lang;
    },

    saveLocale(lang) {
      this.Lang.setLocale(lang);
      ApiService.setHeader('SMS-Locale', lang);
      StorageService.saveItem('locale', lang);
    },

    changeDir(lang) {
      document.documentElement.setAttribute('lang', lang);
      // Force RTL for ar and ar_kw
      const isRTL = lang.startsWith('ar');
      document.documentElement.setAttribute('dir', isRTL ? 'rtl' : 'ltr');

      // Swap Global CSS files (Same as Topbar/Sidebar for consistency)
      const bootstrapLink = document.getElementById('bootstrap-link');
      const mainStyleLink = document.getElementById('main-style-link');
      let customEnLink = document.getElementById('custom-en-link');

      if (bootstrapLink) {
        bootstrapLink.href = isRTL
          ? 'https://cdn.rtlcss.com/bootstrap/v4.1.3/css/bootstrap.min.css'
          : 'https://cdn.jsdelivr.net/npm/bootstrap@4.1.3/dist/css/bootstrap.min.css';
      }

      if (mainStyleLink) {
        mainStyleLink.href = isRTL ? '/assets/css/style-ar.css' : '/assets/css/style-en.css';
      }

      // Swap Vue App CSS
      let cssLink = document.head.querySelector("link[data-app='vuejs']");
      if (cssLink) {
          let path = cssLink.href;
          if (path.includes('app-')) {
             let newHref = path;
             if (isRTL) {
                newHref = newHref.replace('app-en', 'app-ar');
             } else {
                newHref = newHref.replace('app-ar', 'app-en');
             }
             if (newHref !== path) cssLink.href = newHref;
          }
      }

      // Handle Custom English Style if it exists
      if (!isRTL) {
          if (!customEnLink) {
              customEnLink = document.createElement('link');
              customEnLink.rel = 'stylesheet';
              customEnLink.id = 'custom-en-link';
              customEnLink.href = '/assets/front_end/css/new_style/custom_en.css';
              document.head.appendChild(customEnLink);
          }
      } else if (customEnLink) {
          customEnLink.remove();
      }
      
      // Update body class
      let bodyEl = document.body;
      if (isRTL) {
          bodyEl.classList.add('body-rtl');
      } else {
          bodyEl.classList.remove('body-rtl');
      }
    },

    loadSavedLocale() {
      const savedLocale = localStorage.getItem('locale') || 'ar';
      this.currentLocale = savedLocale;
      this.Lang.setLocale(savedLocale);
      ApiService.setHeader('SMS-Locale', savedLocale);
      // Initial Apply
      this.changeDir(savedLocale);
    },
  },
};
</script>
